import { useCallback, useRef } from 'react';
import { rpcClient } from '../api/rpcClient';
import { generateInvoiceEmailRpc } from '../../__generated__/chirp/com/hubspot/commerce/ai/rpc/CommerceGenerationService';
import { recordFeedbackRpc } from '../../__generated__/chirp/com/hubspot/commerce/ai/rpc/FeedbackService';
import { getProperty } from 'customer-data-objects/objects/ObjectAccessors';
import { trackGenerateInvoiceEmail } from '../utils/invoiceUsageTracker';
const INVOICE_EMAIL_FEATURE_ID = '193';

/**
 * Hook for generating AI-powered invoice email content
 * @param invoiceId The ID of the invoice
 */
export const useGenerateInvoiceEmail = invoiceId => {
  const lastGeneratedBodyRef = useRef(null);
  const invoiceEmailInferenceIdRef = useRef(null);
  const handleAiGenerateEmailContent = useCallback(async aiGenerateEmailParams => {
    try {
      var _aiGenerateEmailParam;
      const response = await rpcClient.call(generateInvoiceEmailRpc, {
        request: {
          invoiceId,
          to: {
            email: getProperty(aiGenerateEmailParams.toContact, 'email'),
            firstname: getProperty(aiGenerateEmailParams.toContact, 'firstname'),
            lastname: getProperty(aiGenerateEmailParams.toContact, 'lastname')
          },
          from: {
            name: (_aiGenerateEmailParam = aiGenerateEmailParams.fromValue.name) !== null && _aiGenerateEmailParam !== void 0 ? _aiGenerateEmailParam : undefined,
            from: aiGenerateEmailParams.fromValue.email
          }
        },
        // LlmModule can be slow on the backend, we don't want it to time out
        timeout: 20000
      });
      lastGeneratedBodyRef.current = response.result;
      invoiceEmailInferenceIdRef.current = response.inferenceId || null;

      // Track the AI email generation usage
      trackGenerateInvoiceEmail();
      return {
        // Currently, the backend doesn't return a subject, but may later, leaving this
        // as null for now to simplify changes in future if it starts to support subject.
        subject: null,
        body: response.result
      };
    } catch (error) {
      // Return null values on error to let the UI handle error display
      return {
        subject: null,
        body: null
      };
    }
  }, [invoiceId]);
  const sendAiFeedback = useCallback(async modifiedContent => {
    const inferenceId = invoiceEmailInferenceIdRef.current;
    const originalContent = lastGeneratedBodyRef.current;
    if (!inferenceId || !originalContent) {
      return;
    }
    try {
      await rpcClient.call(recordFeedbackRpc, {
        request: {
          inferenceId,
          feedback: modifiedContent,
          featureId: INVOICE_EMAIL_FEATURE_ID
        }
      });
    } catch (error) {
      console.error('Failed to send AI feedback:', error);
    }
  }, []);
  return {
    handleAiGenerateEmailContent,
    sendAiFeedback,
    lastGeneratedBodyRef,
    invoiceEmailInferenceIdRef
  };
};